import { useMemo } from "react";

import type { IKeywordGroup } from "app/pages/digital-marketing/common/hooks/useKeywordGroups";
import { useTranslation } from "pro-features/components/WithTranslation";

import { KeywordGroupSection } from "./KeywordGroupSection";

interface IKeywordGroupListProps {
    disabledIds: Set<string>;
    deferredSearchValue: string;
    userGroups: IKeywordGroup[];
    sharedGroups: IKeywordGroup[];
    selectedItems: Set<string>;
    onToggle: (groupId: string) => void;
    disabledTooltipContent?: string;
}

export const KeywordGroupList = ({
    disabledIds,
    deferredSearchValue,
    userGroups,
    sharedGroups,
    selectedItems,
    onToggle,
    disabledTooltipContent,
}: IKeywordGroupListProps) => {
    const translate = useTranslation();
    const MAX_INITIAL_ITEMS = 20;

    const normalizedGroups = useMemo(
        () => ({
            userGroups: userGroups.map((group) => ({
                ...group,
                normalizedName: group.Name.toLowerCase(),
            })),
            sharedGroups: sharedGroups.map((group) => ({
                ...group,
                normalizedName: group.Name.toLowerCase(),
            })),
        }),
        [userGroups, sharedGroups],
    );

    const { filteredUserGroups, filteredSharedGroups } = useMemo(() => {
        const search = deferredSearchValue.trim().toLowerCase();

        if (!search || search.length < 2) {
            return {
                filteredUserGroups: normalizedGroups.userGroups.slice(0, MAX_INITIAL_ITEMS),
                filteredSharedGroups: normalizedGroups.sharedGroups.slice(0, MAX_INITIAL_ITEMS),
            };
        }

        const filteredUser: IKeywordGroup[] = [];
        const filteredShared: IKeywordGroup[] = [];

        for (const group of normalizedGroups.userGroups) {
            if (group.normalizedName.includes(search)) {
                filteredUser.push(group);
            }
        }

        for (const group of normalizedGroups.sharedGroups) {
            if (group.normalizedName.includes(search)) {
                filteredShared.push(group);
            }
        }

        return {
            filteredUserGroups: filteredUser,
            filteredSharedGroups: filteredShared,
        };
    }, [normalizedGroups, deferredSearchValue]);

    return (
        <>
            <KeywordGroupSection
                disabledIds={disabledIds}
                title={translate("monitor_and_protect.keywords_group_import.my_lists")}
                groups={filteredUserGroups}
                selectedItems={selectedItems}
                onToggle={onToggle}
                disabledTooltipContent={disabledTooltipContent}
            />
            <KeywordGroupSection
                disabledIds={disabledIds}
                title={translate("monitor_and_protect.keywords_group_import.shared_with_me")}
                groups={filteredSharedGroups}
                selectedItems={selectedItems}
                onToggle={onToggle}
                disabledTooltipContent={disabledTooltipContent}
            />
        </>
    );
};
